import { useRef, useEffect, useContext, useCallback, useState } from "react";
import "./Modal.css";
import { HarvestContext } from "../store/HarvestContext";
import { useAuthUser } from "../hooks/useAuthUser";
import { StepNavPanel, type StepNavPanelHandle } from "./StepNavPanel";
import {
  EditProductForm,
  EDIT_PRODUCT_FORM_ID,
  type EditProductFormSnapshot,
} from "./EditProductForm";

export type EditProductModalBodyProps = {
  idToEdit: string;
  closeModal: () => void;
  renderDeleteButton: boolean;
  onDelete: (id: string) => void;
};

export function EditProductModalBody({
  idToEdit,
  closeModal,
  renderDeleteButton,
  onDelete,
}: EditProductModalBodyProps) {
  const { harvestProducts, updateHarvestProduct } = useContext(HarvestContext);
  const { isAdmin } = useAuthUser();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const stepNavRef = useRef<StepNavPanelHandle>(null);
  const FORM_STEP = 0;
  const UNSAVED_CONFIRM_STEP = 1;
  const DELETE_CONFIRM_STEP = 2;
  const formSnapshotRef = useRef<EditProductFormSnapshot>({
    newQuantity: 0,
    newPrice: 0,
    newDiscount: null,
    hasUnsavedChanges: false,
  });
  const productToEdit = harvestProducts.find((item) => item.id === idToEdit);
  const { quantity, price, discount, id } = productToEdit ?? {
    name: "",
    quantity: 0,
    price: 0,
    discount: null,
    id: "",
  };

  const handleFormValuesChange = useCallback(
    (snapshot: EditProductFormSnapshot) => {
      formSnapshotRef.current = snapshot;
    },
    [],
  );

  const confirmDiscardAndExit = useCallback(() => {
    stepNavRef.current?.goToStep(FORM_STEP);
    closeModal();
  }, [closeModal, FORM_STEP]);

  const handleCancelFromEdit = useCallback(() => {
    if (formSnapshotRef.current.hasUnsavedChanges) {
      stepNavRef.current?.goToStep(UNSAVED_CONFIRM_STEP);
      return;
    }
    closeModal();
  }, [closeModal, UNSAVED_CONFIRM_STEP]);

  const handleBackFromConfirm = useCallback(() => {
    stepNavRef.current?.goToStep(FORM_STEP);
  }, [FORM_STEP]);

  const handleRequestDelete = useCallback(() => {
    stepNavRef.current?.goToStep(DELETE_CONFIRM_STEP);
  }, [DELETE_CONFIRM_STEP]);

  const handleBackFromDeleteConfirm = useCallback(() => {
    stepNavRef.current?.goToStep(FORM_STEP);
  }, [FORM_STEP]);

  const handleConfirmDelete = useCallback(async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await onDelete(id);
      stepNavRef.current?.goToStep(FORM_STEP);
      closeModal();
    } finally {
      setIsDeleting(false);
    }
  }, [onDelete, id, closeModal, FORM_STEP, isDeleting]);

  const handleValidSubmit = useCallback(
    async (q: number, p: number, d: number | null) => {
      if (isSubmitting) return;
      setIsSubmitting(true);
      try {
        await updateHarvestProduct(idToEdit, q, p, d);
        stepNavRef.current?.goToStep(FORM_STEP);
        closeModal();
      } finally {
        setIsSubmitting(false);
      }
    },
    [closeModal, idToEdit, updateHarvestProduct, FORM_STEP, isSubmitting],
  );

  useEffect(() => {
    stepNavRef.current?.goToStep(FORM_STEP);
  }, [quantity, price, discount, idToEdit]);

  const steps = [
    {
      content: (
        <EditProductForm
          productId={idToEdit}
          quantity={quantity}
          price={price}
          discount={discount}
          isAdmin={isAdmin}
          onValidSubmit={handleValidSubmit}
          onValuesChange={handleFormValuesChange}
        />
      ),
      footer: (
        <>
          <div className="buttons-footer">
            <button
              type="button"
              className="btn equal-btns"
              onClick={handleCancelFromEdit}
            >
              Cancel
            </button>

            <button
              type="submit"
              form={EDIT_PRODUCT_FORM_ID}
              className="btn equal-btns"
              disabled={isSubmitting || isDeleting}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>
          {renderDeleteButton && (
            <button
              type="button"
              className="btn delete-btn"
              title="Remove"
              onClick={handleRequestDelete}
            >
              Delete
            </button>
          )}
        </>
      ),
    },
    {
      content: (
        <div className="edit-inputs">
          <p className="modal-exit-confirm-msg">
            Are you sure you want to exit with unsaved changes?
          </p>
        </div>
      ),
      footer: (
        <div className="buttons-footer">
          <button
            type="button"
            className="btn equal-btns"
            onClick={handleBackFromConfirm}
          >
            No
          </button>
          <button
            type="button"
            className="btn equal-btns"
            onClick={confirmDiscardAndExit}
          >
            Yes
          </button>
        </div>
      ),
    },
    {
      content: (
        <div className="edit-inputs">
          <p className="modal-exit-confirm-msg">
            Are you sure you want to delete this product?
          </p>
        </div>
      ),
      footer: (
        <div className="buttons-footer">
          <button
            type="button"
            className="btn equal-btns"
            onClick={handleBackFromDeleteConfirm}
          >
            No
          </button>
          <button
            type="button"
            className="btn equal-btns"
            disabled={isDeleting}
            onClick={() => void handleConfirmDelete()}
          >
            {isDeleting ? "Deleting..." : "Yes, delete"}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="modal-content-wrapper">
      <StepNavPanel
        ref={stepNavRef}
        steps={steps}
        className="edit-product-step-nav"
      />
    </div>
  );
}
